import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, OrganizationSchema } from 'components/SEO';
import { useTransactionFeeRate } from 'hooks/useTransactionFeeRate';
import { eliteFeeCopy, formatFeePct } from 'config/economyCopy';
import {
  ABOUT_COMPARE_LINE,
  ABOUT_DIFFERENT,
  ABOUT_DOES,
  ABOUT_FAQ_CLOSING,
  ABOUT_FAQ_IS_FREE_HEADING,
  ABOUT_FAQ_STATIC,
  ABOUT_H1,
  ABOUT_H2,
  ABOUT_HOW,
  ABOUT_INTRO,
  ABOUT_LINKEDIN_LINK_TEXT,
  ABOUT_LINKS,
  ABOUT_SEO,
  ABOUT_TEAM,
  ABOUT_WHO,
  AboutItem,
  KeyFactValue,
  isFreeAnswer,
  keyFacts,
} from './aboutCopy';
import './about.css';

/**
 * /about (Asana 1219261192406735): one H1, seven H2 sections in the ticket's
 * order, H3 items, a Key Facts definition list and Organization JSON-LD. Live
 * fee values come from the public rate read and render without a number while
 * loading or after a failed read, never a fallback figure.
 */
const Items: React.FC<{ items: AboutItem[] }> = ({ items }) => (
  <>
    {items.map((item) => (
      <div key={item.heading} className="about-item">
        <h3 className="about-item-heading">{item.heading}</h3>
        <p className="about-item-body">{item.body}</p>
      </div>
    ))}
  </>
);

const FactValue: React.FC<{ value: KeyFactValue }> = ({ value }) => {
  if ('links' in value) {
    return (
      <>
        {value.links.map((link, index) => (
          <React.Fragment key={link.href}>
            {index > 0 && ', '}
            <a href={link.href} target="_blank" rel="noopener noreferrer">
              {link.text}
            </a>
          </React.Fragment>
        ))}
      </>
    );
  }
  if ('href' in value) {
    return value.external ? (
      <a href={value.href} target="_blank" rel="noopener noreferrer">
        {value.text}
      </a>
    ) : (
      <Link to={value.href}>{value.text}</Link>
    );
  }
  return <>{value.text}</>;
};

const AboutPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { ratePct, eliteDiscountPct } = useTransactionFeeRate();
  const feeLabel = ratePct != null ? formatFeePct(ratePct) : undefined;
  const eliteFee =
    ratePct != null && eliteDiscountPct != null ? eliteFeeCopy(ratePct, eliteDiscountPct) : undefined;
  const live = { feeLabel, eliteFee };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleNavigationAttempt = () => {
    if (!currentUser) setShowLoginModal(true);
  };

  return (
    <>
      <SEOHelmet
        title={ABOUT_SEO.title}
        description={ABOUT_SEO.description}
        canonical={ABOUT_SEO.canonical}
        type="website"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'About', url: ABOUT_SEO.canonical },
        ]}
      />
      <OrganizationSchema />

      <div className="about-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="about-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="about-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/about', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="about-main">
            <TopNavigationBar
              title="About"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={() => setIsMobileMenuOpen((open) => !open)}
            />

            <article className="about-container">
              <header className="about-header">
                <h1 className="about-title">{ABOUT_H1}</h1>
                <p className="about-intro">{ABOUT_INTRO}</p>
              </header>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.does}</h2>
                <Items items={ABOUT_DOES} />
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.different}</h2>
                <Items items={ABOUT_DIFFERENT} />
                <p className="about-closing">
                  {ABOUT_COMPARE_LINE.before}
                  <Link to={ABOUT_COMPARE_LINE.to}>{ABOUT_COMPARE_LINE.link}</Link>
                  {ABOUT_COMPARE_LINE.after}
                </p>
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.who}</h2>
                <ul className="about-list">
                  {ABOUT_WHO.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.team}</h2>
                {ABOUT_TEAM.map((item, index) => (
                  <div key={item.heading} className="about-item">
                    <h3 className="about-item-heading">{item.heading}</h3>
                    <p className="about-item-body">
                      {item.body}
                      {index === 0 && (
                        <>
                          {' '}
                          <a href={ABOUT_LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                            {ABOUT_LINKEDIN_LINK_TEXT}
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.how}</h2>
                <Items items={ABOUT_HOW} />
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.facts}</h2>
                <dl className="about-facts" data-testid="about-key-facts">
                  {keyFacts(live).map((fact) => (
                    <div key={fact.label} className="about-fact">
                      <dt>{fact.label}</dt>
                      <dd>
                        <FactValue value={fact.value} />
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="about-section">
                <h2 className="about-section-title">{ABOUT_H2.faq}</h2>
                <div className="about-item">
                  <h3 className="about-item-heading">{ABOUT_FAQ_IS_FREE_HEADING}</h3>
                  <p className="about-item-body">{isFreeAnswer(live)}</p>
                </div>
                <Items items={ABOUT_FAQ_STATIC} />
                <p className="about-closing">
                  {ABOUT_FAQ_CLOSING.before}
                  <Link to={ABOUT_FAQ_CLOSING.to}>{ABOUT_FAQ_CLOSING.link}</Link>
                  {ABOUT_FAQ_CLOSING.after}
                </p>
              </section>
            </article>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
